import axios from "axios";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTenant } from "../hooks";
import { useTranslation } from "react-i18next";

const periodsContext = createContext();
const DEFAULT_PER_PAGE = 15;

const getDefaultPagination = () => ({
  enabled: false,
  currentPage: 1,
  totalPages: 1,
  perPage: DEFAULT_PER_PAGE,
  total: null,
});

const toPositiveInt = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return Math.trunc(parsed);
};

const toInputDate = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const toApiDate = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const normalizePeriod = (period = {}) => {
  const finStatusNumber = Number(period.fin_status);
  const closedStatus = finStatusNumber === 1 ? "opened" : "closed";
  const state = period.is_current ? "active" : "inactive";

  return {
    ...period,
    id: period.id,
    fin_start_date: toApiDate(period.fin_start_date),
    fin_end_date: toApiDate(period.fin_end_date),
    end_date_until: toApiDate(period.end_date_until),
    startDateInput: toInputDate(period.fin_start_date),
    endDateInput: toInputDate(period.fin_end_date),
    closedUntilDateInput: toInputDate(period.end_date_until),
    startDateDisplay: toInputDate(period.fin_start_date),
    endDateDisplay: toInputDate(period.fin_end_date),
    closedUntilDateDisplay: toInputDate(period.end_date_until),
    state,
    closeduntil: closedStatus,
  };
};

const extractApiPayload = (responseData) => {
  const pagePayload = responseData?.data || {};
  const rawPeriods = Array.isArray(pagePayload.data) ? pagePayload.data : [];

  const currentPage = toPositiveInt(pagePayload.current_page) || 1;
  const totalPages = toPositiveInt(pagePayload.last_page) || 1;
  const perPage = toPositiveInt(pagePayload.per_page);
  const total = toPositiveInt(pagePayload.total);

  return {
    rawPeriods,
    pagination: {
      enabled: currentPage > 0 && totalPages > 0,
      currentPage,
      totalPages,
      perPage: perPage || DEFAULT_PER_PAGE,
      total: total || null,
    },
  };
};

export const PeriodsProvider = ({ children }) => {
  const { t } = useTranslation("global");
  const BaseUrl = process.env.REACT_APP_INV_API_SERVER_BASE_URL;
  const { tenantId, companySelected, branchSelected } = useTenant();
  const [fetched, setFetched] = useState(false);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverPagination, setServerPagination] = useState(
    getDefaultPagination(),
  );

  const canQuery = useMemo(
    () => Boolean(tenantId && companySelected?.value),
    [tenantId, companySelected?.value],
  );

  const fetchPeriods = useCallback(
    async (options = {}) => {
      if (!canQuery) {
        setPeriods([]);
        setServerPagination(getDefaultPagination());
        setFetched(false);
        return;
      }

      try {
        setLoading(true);
        const requestPayload = {
          tenant_id: tenantId,
          company_id: companySelected?.value,
        };

        const requestedPage = toPositiveInt(options?.page);
        const requestedPerPage =
          toPositiveInt(options?.perPage) || DEFAULT_PER_PAGE;
        if (requestedPage) requestPayload.page = requestedPage;
        if (requestedPerPage) requestPayload.per_page = requestedPerPage;

        const response = await axios.post(
          `${BaseUrl}/fin-periods`,
          requestPayload,
        );

        const { rawPeriods, pagination } = extractApiPayload(response?.data);
        setPeriods(rawPeriods.map(normalizePeriod));
        setServerPagination(pagination);
        setFetched(true);
      } catch (err) {
        console.error(
          "Failed to fetch financial periods",
          err?.response?.data || err?.message || err,
        );
        setPeriods([]);
        setServerPagination(getDefaultPagination());
        setFetched(false);
      } finally {
        setLoading(false);
      }
    },
    [BaseUrl, canQuery, tenantId, companySelected?.value],
  );

  const savePeriod = useCallback(
    async (payload) => {
      if (!canQuery) {
        throw new Error("Missing tenant/company while saving financial period");
      }

      const requestPayload = {
        fin_start_date: toApiDate(payload?.fin_start_date),
        fin_end_date: toApiDate(payload?.fin_end_date),
        tenant_id: tenantId,
        company_id: companySelected?.value,
      };

      if (payload?.id) {
        requestPayload.id = payload.id;
      }

      try {
        setSaving(true);
        const response = await axios.post(
          `${BaseUrl}/fin-periods/store`,
          requestPayload,
        );
        await fetchPeriods({
          page: serverPagination.currentPage,
          perPage: serverPagination.perPage,
        });
        return response;
      } catch (err) {
        console.error(
          "Failed to save financial period",
          err?.response?.data || err?.message || err,
        );
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [
      BaseUrl,
      canQuery,
      tenantId,
      companySelected?.value,
      fetchPeriods,
      serverPagination.currentPage,
      serverPagination.perPage,
    ],
  );

  const deletePeriod = useCallback(
    async (id) => {
      if (!id) return null;

      try {
        setSaving(true);
        const response = await axios.get(`${BaseUrl}/fin-periods/delete/${id}`);
        await fetchPeriods({
          page: serverPagination.currentPage,
          perPage: serverPagination.perPage,
        });
        return response;
      } catch (err) {
        console.error(
          "Failed to delete financial period",
          err?.response?.data || err?.message || err,
        );
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [
      BaseUrl,
      fetchPeriods,
      serverPagination.currentPage,
      serverPagination.perPage,
    ],
  );
  const closedPeriod = useCallback(
    async (id) => {
      if (!id) return null;

      // Check if period exists and is not already closed
      const period = periods.find((p) => p.id === id);

      if (!period) {
        throw new Error(t("times.period_not_found"));
      }

      if (period.closeduntil === "closed") {
        throw new Error(t("times.period_already_closed"));
      }

      if (period.is_current) {
        throw new Error(t("times.cannot_close_current_period"));
      }

      const requestPayload = {
        fin_year_id: id,
        branch_id: branchSelected?.value,
        tenant_id: tenantId,
        company_id: companySelected?.value,
      };
      try {
        setSaving(true);
        const response = await axios.post(
          `${BaseUrl}/fin-periods/closed`,
          requestPayload,
        );

        await fetchPeriods({
          page: serverPagination.currentPage,
          perPage: serverPagination.perPage,
        });

        return response;
      } catch (err) {
        console.error(
          "Failed to close financial period",
          err?.response?.data || err?.message || err,
        );
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [
      periods,
      branchSelected?.value,
      tenantId,
      companySelected?.value,
      BaseUrl,
      fetchPeriods,
      serverPagination.currentPage,
      serverPagination.perPage,
    ],
  );
  const defualtPeriod = useCallback(
    async (id) => {
      if (!id) return null;

      // Check if period exists and is not closed
      const period = periods.find((p) => p.id === id);
      if (!period) {
        throw new Error(t("times.period_not_found"));
      }
      if (period.closeduntil === "closed") {
        throw new Error(t("times.period_already_closed"));
      }
      if (period.is_current) {
        throw new Error(t("times.cannot_close_current_period"));
      }

      const requestPayload = {
        fin_year_id: id,
        branch_id: branchSelected?.value,
        tenant_id: tenantId,
        company_id: companySelected?.value,
      };
      try {
        setSaving(true);
        const response = await axios.post(
          `${BaseUrl}/fin-periods/current`,
          requestPayload,
        );

        await fetchPeriods({
          page: serverPagination.currentPage,
          perPage: serverPagination.perPage,
        });
        return response;
      } catch (err) {
        console.error(
          "Failed to set default financial period",
          err?.response?.data || err?.message || err,
        );
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [
      periods,
      branchSelected?.value,
      tenantId,
      companySelected?.value,
      BaseUrl,
      fetchPeriods,
      serverPagination.currentPage,
      serverPagination.perPage,
    ],
  );
  useEffect(() => {
    fetchPeriods();
  }, [fetchPeriods]);

  return (
    <periodsContext.Provider
      value={{
        periods,
        loading,
        saving,
        serverPagination,
        fetchPeriods,
        savePeriod,
        deletePeriod,
        fetched,
        closedPeriod,
        defualtPeriod,
      }}
    >
      {children}
    </periodsContext.Provider>
  );
};

export default periodsContext;
